import React, { useState } from "react";
import {
    Container,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Box,
    Stack,
} from "@mui/material";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import axios from "axios";
import QRCode from "qrcode";
import { API_URL } from "../config";
import { SectionHeading } from "../utils";

const RECORD_NAME_MAX_LENGTH = 25;

const normalizeHttpUrl = (value) => {
    const trimmed = value.trim();
    if (!trimmed) throw new Error("URL is required.");

    const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(withScheme);

    if (!["http:", "https:"].includes(parsed.protocol)) {
        throw new Error("Please enter a valid http or https URL.");
    }

    return parsed.toString();
};

const UploadURLQR = () => {
    const [url, setUrl] = useState("");
    const [name, setName] = useState("");
    const [qrCode, setQrCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const handleGenerateQR = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage("");

        const trimmedName = name.trim();

        if (trimmedName.length > RECORD_NAME_MAX_LENGTH) {
            setErrorMessage(`Name must be ${RECORD_NAME_MAX_LENGTH} characters or fewer.`);
            setQrCode("");
            setLoading(false);
            return;
        }

        try {
            const normalizedUrl = normalizeHttpUrl(url);
            const generatedQr = await QRCode.toDataURL(normalizedUrl, { width: 340, margin: 1, errorCorrectionLevel: "H" });
            setUrl(normalizedUrl);
            setQrCode(generatedQr);
            setErrorMessage("");
        } catch (error) {
            setErrorMessage(error.message || "Failed to generate QR code. Please check the URL and try again.");
            setQrCode("");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveQR = async () => {
        setSaving(true);
        setSuccessMessage("");

        const trimmedName = name.trim();

        if (trimmedName.length > RECORD_NAME_MAX_LENGTH) {
            setErrorMessage(`Name must be ${RECORD_NAME_MAX_LENGTH} characters or fewer.`);
            setSaving(false);
            return;
        }

        try {
            const normalizedUrl = normalizeHttpUrl(url);
            const formData = new FormData();
            formData.append("name", trimmedName);
            formData.append("url", normalizedUrl);

            const response = await axios.post(
                API_URL + "upload_url_qr.php",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                }
            );

            if (response.data.success) {
                setUrl(normalizedUrl);
                setSuccessMessage(response.data.message || "URL QR code saved successfully.");
                setErrorMessage("");
            } else {
                setErrorMessage(response.data.message || "Failed to save QR code. Please try again.");
            }
        } catch (error) {
            setErrorMessage(error.response?.data?.message || error.message || "Failed to save QR code. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Container maxWidth="md" className="form-shell upload-url-shell">
            <Box className="form-shell-badge">
                <LinkRoundedIcon fontSize="small" />
                <Typography variant="caption">URL QR generator</Typography>
            </Box>

            <SectionHeading sx={{ marginTop: "-8px" }}>
                Create a sharper QR destination
            </SectionHeading>

            <Typography variant="h6" gutterBottom className="form-subheading">
                Add a memorable label, paste the destination, and generate a scannable QR code instantly.
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} className="form-feature-row">
                <Box className="form-feature-card">
                    <QrCode2RoundedIcon fontSize="small" />
                    <Box>
                        <Typography variant="subtitle2">Instant preview</Typography>
                        <Typography variant="body2">Review your generated code before saving it.</Typography>
                    </Box>
                </Box>
                <Box className="form-feature-card">
                    <TravelExploreRoundedIcon fontSize="small" />
                    <Box>
                        <Typography variant="subtitle2">Link-ready</Typography>
                        <Typography variant="body2">Perfect for campaigns, menus, posters, and landing pages.</Typography>
                    </Box>
                </Box>
            </Stack>

            <form onSubmit={handleGenerateQR} className="form-stack">
                <Stack spacing={1.5}>
                    <TextField
                        label="Name"
                        helperText={`${name.length}/${RECORD_NAME_MAX_LENGTH} characters`}
                        value={name}
                        onChange={(e) => setName(e.target.value.slice(0, RECORD_NAME_MAX_LENGTH))}
                        fullWidth
                        required
                        variant="outlined"
                        className="form-input"
                        inputProps={{ maxLength: RECORD_NAME_MAX_LENGTH }}
                    />

                    <TextField
                        label="Enter URL"
                        helperText="Paste a full URL or domain. Domains are saved with https:// automatically."
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        fullWidth
                        required
                        variant="outlined"
                        className="form-input"
                    />
                </Stack>

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    className="form-submit-btn"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Generate QR Code"}
                </Button>
            </form>

            {qrCode && (
                <Box className="qr-preview-block">
                    <Box className="qr-preview-header">
                        <Typography variant="h6">Your QR Code</Typography>
                        <Typography variant="body2">
                            Looks good? Save it to your record library for later use.
                        </Typography>
                    </Box>
                    <img
                        src={qrCode}
                        alt="Generated QR Code"
                        className="qr-preview-image"
                    />
                    <Button
                        variant="outlined"
                        className="qr-save-btn"
                        onClick={handleSaveQR}
                        disabled={saving}
                    >
                        {saving ? <CircularProgress size={24} color="inherit" /> : "Save QR Code"}
                    </Button>
                </Box>
            )}

            {successMessage && (
                <Snackbar
                    open={true}
                    autoHideDuration={4000}
                    onClose={() => setSuccessMessage("")}
                >
                    <Alert severity="success" onClose={() => setSuccessMessage("")}>{successMessage}</Alert>
                </Snackbar>
            )}

            {errorMessage && (
                <Snackbar
                    open={true}
                    autoHideDuration={4000}
                    onClose={() => setErrorMessage("")}
                >
                    <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert>
                </Snackbar>
            )}
        </Container>
    );
};

export default UploadURLQR;
